import React, { useMemo, useState } from 'react';
import type { Player } from '../../types';
import { theme } from '../../theme';
import { useResponsive } from '../../hooks/useResponsive';

type Step = 'main' | 'swap-team1' | 'swap-team2';

interface TeamsLike {
  team1: (Player | null)[];
  team2: (Player | null)[];
}

interface MovePlayerModalProps {
  isOpen: boolean;
  player: Player | null;
  teams: TeamsLike;
  substitutes: Player[];
  team1Name: string;
  team2Name: string;
  maxPlayers: number;
  team1Color: string;
  team2Color: string;
  onClose: () => void;
  /** Move to other team — auto-pick first empty slot or first non-self player */
  onMovePlayer: (playerId: string, teamIndex: number, positionIndex: number) => Promise<void> | void;
  /** Swap two specific players */
  onSwapPlayers: (currentPlayerId: string, targetPlayerId: string) => Promise<void> | void;
  /** Toggle isSubstitute (swap-players with p1==p2 on the backend) */
  onMoveToSubstitutes: (playerId: string) => Promise<void> | void;
}

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

export const MovePlayerModal: React.FC<MovePlayerModalProps> = ({
  isOpen,
  player,
  teams,
  substitutes,
  team1Name,
  team2Name,
  maxPlayers,
  team1Color,
  team2Color,
  onClose,
  onMovePlayer,
  onSwapPlayers,
  onMoveToSubstitutes,
}) => {
  const { isMobile } = useResponsive();
  const [step, setStep] = useState<Step>('main');
  const [loading, setLoading] = useState(false);

  const currentTeam = useMemo<'team1' | 'team2' | 'substitutes' | null>(() => {
    if (!player) return null;
    if (teams.team1.some((p) => p?.id === player.id)) return 'team1';
    if (teams.team2.some((p) => p?.id === player.id)) return 'team2';
    if (substitutes.some((p) => p.id === player.id)) return 'substitutes';
    return null;
  }, [player, teams, substitutes]);

  if (!isOpen || !player) return null;

  const isInTeam1 = currentTeam === 'team1';
  const isInTeam2 = currentTeam === 'team2';
  const isInSubs = currentTeam === 'substitutes';
  const canSwapTeam1 = !isInTeam1 && teams.team1.some((p) => p && p.id !== player.id);
  const canSwapTeam2 = !isInTeam2 && teams.team2.some((p) => p && p.id !== player.id);
  const targetTeam1HasRoom = teams.team1.filter((p) => p !== null).length < maxPlayers;
  const targetTeam2HasRoom = teams.team2.filter((p) => p !== null).length < maxPlayers;
  const canMoveToTeam1 = !isInTeam1 && (targetTeam1HasRoom || teams.team1.some((p) => p !== null));
  const canMoveToTeam2 = !isInTeam2 && (targetTeam2HasRoom || teams.team2.some((p) => p !== null));
  const canMoveToSubs = !isInSubs;

  const teamColorOf = (which: 'team1' | 'team2' | 'substitutes' | null) => {
    if (which === 'team1') return team1Color;
    if (which === 'team2') return team2Color;
    return theme.colors.textSecondary;
  };

  const close = () => {
    setStep('main');
    onClose();
  };

  const handleMoveToOther = async (target: 'team1' | 'team2') => {
    const targetPlayers = target === 'team1' ? teams.team1 : teams.team2;
    const targetIdx = target === 'team1' ? 0 : 1;
    let positionIndex = -1;
    for (let i = 0; i < maxPlayers; i++) {
      if (!targetPlayers[i]) {
        positionIndex = i;
        break;
      }
    }
    if (positionIndex === -1) {
      for (let i = 0; i < maxPlayers; i++) {
        if (targetPlayers[i] && targetPlayers[i]?.id !== player.id) {
          positionIndex = i;
          break;
        }
      }
    }
    if (positionIndex === -1) return;
    setLoading(true);
    try {
      await onMovePlayer(player.id, targetIdx, positionIndex);
      close();
    } finally {
      setLoading(false);
    }
  };

  const handleSwapWith = async (targetPlayer: Player) => {
    setLoading(true);
    try {
      await onSwapPlayers(player.id, targetPlayer.id);
      close();
    } finally {
      setLoading(false);
    }
  };

  const handleSubstituteToggle = async () => {
    setLoading(true);
    try {
      await onMoveToSubstitutes(player.id);
      close();
    } finally {
      setLoading(false);
    }
  };

  const actionRow = (
    iconBgColor: string,
    icon: string,
    title: string,
    subtitle: string,
    onClick: () => void
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: theme.colors.background,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 12,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: `${iconBgColor}33`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconBgColor,
          fontSize: 18,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{title}</div>
        <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
      <span style={{ color: theme.colors.textSecondary, fontSize: 18 }}>›</span>
    </button>
  );

  const renderSwapList = (target: 'team1' | 'team2') => {
    const targetPlayers = (target === 'team1' ? teams.team1 : teams.team2).filter(
      (p): p is Player => !!p && p.id !== player.id
    );
    const teamColor = target === 'team1' ? team1Color : team2Color;
    const teamLabel = target === 'team1' ? team1Name : team2Name;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>
          Intercambiar con jugador de {teamLabel}
        </div>
        <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginBottom: 6 }}>
          Toca un jugador para intercambiarlo con {player.name}.
        </div>
        {targetPlayers.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              color: theme.colors.textSecondary,
              padding: 16,
            }}
          >
            No hay jugadores en {teamLabel}.
          </div>
        ) : (
          targetPlayers.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSwapWith(p)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                background: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 12,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                width: '100%',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: teamColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                {initialsOf(p.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.colors.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.name}
                </div>
                {p.rating !== undefined && (
                  <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                    {Array.from({ length: 5 })
                      .map((_, i) => (i < Math.floor(p.rating!) ? '★' : '☆'))
                      .join('')}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 16, color: theme.colors.textSecondary }}>🔄</span>
            </button>
          ))
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="move-player-modal"
      onClick={close}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? 0 : 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.surface,
          borderRadius: isMobile ? '20px 20px 0 0' : theme.borderRadius.card,
          width: '100%',
          maxWidth: isMobile ? '100%' : 480,
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.colors.shadow,
        }}
      >
        {isMobile && (
          <div
            style={{
              width: 40,
              height: 4,
              background: theme.colors.border,
              borderRadius: 2,
              margin: '12px auto 8px',
            }}
          />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 18px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          {step !== 'main' ? (
            <button
              type="button"
              onClick={() => setStep('main')}
              aria-label="Volver"
              style={{
                background: 'none',
                border: 'none',
                fontSize: 20,
                cursor: 'pointer',
                color: theme.colors.textSecondary,
                padding: '4px 8px',
              }}
            >
              ‹
            </button>
          ) : (
            <span style={{ width: 32 }} />
          )}
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
            Cambiar de equipo
          </h3>
          <button
            type="button"
            onClick={close}
            aria-label="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 22,
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              padding: '4px 8px',
            }}
          >
            ×
          </button>
        </div>

        {/* Player header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            padding: 18,
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: teamColorOf(currentTeam),
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 22,
            }}
          >
            {initialsOf(player.name)}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
            {player.name}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>
            {currentTeam === 'team1' && team1Name}
            {currentTeam === 'team2' && team2Name}
            {currentTeam === 'substitutes' && 'Suplente'}
          </div>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {step === 'main' && (
            <>
              {isInTeam1 && canMoveToTeam2 &&
                actionRow(
                  team2Color,
                  '➡️',
                  `Mover a ${team2Name}`,
                  'Llena el primer slot disponible',
                  () => handleMoveToOther('team2')
                )}
              {isInTeam2 && canMoveToTeam1 &&
                actionRow(
                  team1Color,
                  '➡️',
                  `Mover a ${team1Name}`,
                  'Llena el primer slot disponible',
                  () => handleMoveToOther('team1')
                )}
              {canSwapTeam1 &&
                actionRow(
                  team1Color,
                  '🔄',
                  `Intercambiar con ${team1Name}`,
                  'Elige qué jugador del otro equipo',
                  () => setStep('swap-team1')
                )}
              {canSwapTeam2 &&
                actionRow(
                  team2Color,
                  '🔄',
                  `Intercambiar con ${team2Name}`,
                  'Elige qué jugador del otro equipo',
                  () => setStep('swap-team2')
                )}
              {canMoveToSubs &&
                actionRow(
                  '#FFC107',
                  '⬇️',
                  'Mover a suplentes',
                  'Quitarlo de la titularidad',
                  handleSubstituteToggle
                )}
              {isInSubs &&
                actionRow(
                  '#FFC107',
                  '⬆️',
                  'Volver a titulares',
                  'Promuévelo desde suplentes',
                  handleSubstituteToggle
                )}
            </>
          )}
          {step === 'swap-team1' && renderSwapList('team1')}
          {step === 'swap-team2' && renderSwapList('team2')}
        </div>
      </div>
    </div>
  );
};

export default MovePlayerModal;
